import classNames from "classnames";
import { Entry } from "contentful";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";

import IconArrowRight from "@/icons/icon-arrow-right";
import IconInclinedStar from "@/icons/icon-inclined-star";
import IconPlay from "@/icons/icon-play";
import IconPlayFull from "@/icons/icon-play-full";
import EscapeNewLine from "~/components/common/escape-new-line";
import HighlightedText from "~/components/common/highlighted-text";
import Icon from "~/components/common/icon";
import SingleAsset from "~/components/common/single-asset";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import { isActiveEntry } from "~/utils/editorial-utils";
import textUtils from "~/utils/text-utils";

import buttonStyles from "../../common/button.module.scss";
import styles from "./editorial-card.module.scss";

type WrapperCardProps = {
  children: JSX.Element;
  link?: Entry<Editorials.Link>;
  firstCardIsHighlighted: boolean;
};

export function WrapperCard({ children, link, firstCardIsHighlighted }: WrapperCardProps) {
  const router = useRouter();
  return (
    <>
      {link && isActiveEntry(link) ? (
        <Link
          href={textUtils.sanitizeContentfulUrl(link, router)}
          target={link.fields.openOnANewTab ? "_blank" : undefined}
          className={classNames(
            styles.containerCard,
            firstCardIsHighlighted ? styles.firstCardIsHighlighted : undefined
          )}
        >
          {children}
        </Link>
      ) : (
        <div
          className={classNames(
            styles.containerCard,
            firstCardIsHighlighted ? styles.firstCardIsHighlighted : undefined
          )}
        >
          {children}
        </div>
      )}
    </>
  );
}

type Props = {
  entry: Entry<Editorials.Card>;
  firstCardIsHighlighted?: boolean;
};

export default function EditorialCard({ entry, firstCardIsHighlighted = false }: Props) {
  const t = useTranslations();
  const filterVideoTag = Constants.FILTER_CARDS_STANDARD.video;

  const { tag, date, media, singleAsset, title, titleIsBig, description, link } = entry.fields;
  const inspectorMode = contentfulUtils.useInspectorMode(entry);
  const inspectorModeLink = contentfulUtils.useInspectorMode(link);

  const isCardWithFilterVideo = entry.metadata.tags.find((tag) => tag.sys.id === filterVideoTag);

  return (
    <WrapperCard link={link} firstCardIsHighlighted={firstCardIsHighlighted}>
      <>
        {media || singleAsset ? (
          <div className={classNames(styles.cardImg, firstCardIsHighlighted ? styles.cardImgHighlighted : undefined)}>
            <SingleAsset entry={singleAsset} entryFallback={media} fill />
            {isCardWithFilterVideo && (
              <div className={styles.playIcon}>
                <Icon icon={IconPlayFull} width={16} height={16} />
              </div>
            )}
          </div>
        ) : null}
        <div
          className={classNames(
            styles.containerText,
            firstCardIsHighlighted ? styles.containerTextHighlighted : undefined
          )}
        >
          {date ? (
            <p {...inspectorMode?.getProps("date")} className={styles.date}>
              <EscapeNewLine text={date} />
            </p>
          ) : null}
          {tag ? (
            <p {...inspectorMode?.getProps("tag")} className={styles.tag}>
              <EscapeNewLine text={tag} />
            </p>
          ) : null}
          {entry.metadata.tags.length > 0 && (
            <div className={styles.contentfulTags}>
              {entry.metadata.tags.map((contentfulTag, i) => {
                if (contentfulTag.sys.id === filterVideoTag) {
                  return null;
                }
                return (
                  <div key={contentfulTag.sys.id} className={styles.contentfulTag}>
                    {!!i && <Icon icon={IconInclinedStar} width={6} height={6} />}
                    <p>{"#" + t("generic." + contentfulTag.sys.id)}</p>
                  </div>
                );
              })}
            </div>
          )}
          {contentfulUtils.isHighlightText(title) ? (
            <p {...inspectorMode?.getProps("title")} className={classNames(styles.titleTextHighlight)}>
              <HighlightedText text={title} />
            </p>
          ) : (
            <p
              {...inspectorMode?.getProps("title")}
              className={classNames(
                styles.title,
                titleIsBig ? styles.titleIsBig : undefined,
                firstCardIsHighlighted ? styles.titleHighlighted : undefined
              )}
            >
              <EscapeNewLine text={title} />
            </p>
          )}

          {description ? (
            <p {...inspectorMode?.getProps("description")} className={styles.description}>
              <EscapeNewLine text={description} />
            </p>
          ) : null}
          {link && isActiveEntry(link) ? (
            <p
              className={classNames(buttonStyles.button, buttonStyles.secondary, styles.linkAsButton)}
              {...inspectorModeLink?.getProps("text")}
            >
              <span className={styles.textLink}>{link.fields.text} </span>
              <Icon icon={isCardWithFilterVideo ? IconPlay : IconArrowRight} width={16} height={16} aria-hidden />
            </p>
          ) : null}
        </div>
      </>
    </WrapperCard>
  );
}
